import { FC } from "react";
import { BidIntentStatus } from "../page-quotation/BidIntentControls/BidIntentControls";
import styles2 from '../page-dashboard-V2/preflight.module.scss';
import moment from "moment";
import { LineChart } from "../../components/shared/Charts/LineChart/LineChart";
import SummaryCard from "../../components/shared/Charts/SummaryCard/SummaryCard";
import type { Request } from "./types";
import { Color } from "@tremor/react";
import _ from "lodash";
import { useTranslation } from "react-i18next";

type Props = {
  requests: Request[],
  wonPurchaseDates?: string[],
  adStartDate?: Date,
}

const ReceivedBidRequestStats: FC<Props> = ({ requests, wonPurchaseDates = [], adStartDate }) => {
  const [t] = useTranslation('requests', { keyPrefix: 'received.statistics' });
  const now = Date.now();

  const inProgressRequests = requests.filter((r) => new Date(r.timeLeft).getTime() >= now);

  const ongoingUnanswered: Request[] = [];
  const ongoingAccepted: Request[] = [];
  const ongoingDeclined: Request[] = [];
  const ongoingBids: Request[] = [];

  for (const request of inProgressRequests) {
    if (request.hasBid) {
      ongoingBids.push(request);
    } else if (!request.answered && request.intent === BidIntentStatus.UNDECIDED) {
      ongoingUnanswered.push(request);
    } else if (request.answered && request.intent === BidIntentStatus.ACCEPTED) {
      ongoingAccepted.push(request);
    } else if (request.answered && request.intent === BidIntentStatus.DECLINED) {
      ongoingDeclined.push(request);
    }
  }

  const weekFormat = t('weekFormat', '[Vecka] W GGGG');

  const weeklyRequests = _.groupBy(requests, r => moment(r.date).format(weekFormat));
  const weeklyBids = _.groupBy(requests.filter((r) => r.hasBid), r => moment(r.date).format(weekFormat));

  type perWeekLabelsType = { week: string, requests: string, bids: string, requestsSinceAd: string, bidsSinceAd: string };
  const perWeekLabels: perWeekLabelsType = t('perWeek.label', { returnObjects: true }) as perWeekLabelsType;

  let chartData: Record<string, string | number | boolean | null>[] = [];

  if (requests.length) {
    const dates = requests.map((r) => new Date(r.date).getTime());
    const firstDate = Math.min.apply(this, dates);
    const lastDate = Math.max.apply(this, dates);
    let date = moment(firstDate);
    const lastWeek = moment(lastDate).format(weekFormat);
    const weeks = [];
    let week = "";
    let i = 0;
    while (week != lastWeek && i < 10000) {
      week = date.format(weekFormat);
      weeks.push(week);
      date = date.add(7, "days");
      i++;
    }
    const adStartWeek = moment(adStartDate).format('W GGGG');
    const [startWeek, startYear] = adStartWeek.split(' ').map(x => parseInt(x));
    const startDate = parseInt(`${startYear}${startWeek < 10 ? `0${startWeek}` : startWeek}`);

    chartData = weeks.map((w: string) => {
      const [, week, year] = w.split(' ').map(x => parseInt(x));
      const dateString = parseInt(`${year}${week < 10 ? `0${week}` : week}`);

      return (startDate && startDate <= dateString) ? {
        [perWeekLabels.week]: w,
        [perWeekLabels.requests]: null,
        [perWeekLabels.bids]: null,
        [perWeekLabels.requestsSinceAd]: weeklyRequests[w]?.length ?? 0,
        [perWeekLabels.bidsSinceAd]: weeklyBids[w]?.length ?? 0,
        isAfterAd: true,
      } : {
        [perWeekLabels.week]: w,
        [perWeekLabels.requests]: weeklyRequests[w]?.length ?? 0,
        [perWeekLabels.bids]: weeklyBids[w]?.length ?? 0,
        [perWeekLabels.requestsSinceAd]: null,
        [perWeekLabels.bidsSinceAd]: null,
        isAfterAd: false,
      }
    });
  }

  const lineChartTitles = [perWeekLabels.requests, perWeekLabels.bids];
  const lineChartColors: Color[] = ['orange', 'cyan',];

  lineChartTitles.push(perWeekLabels.requestsSinceAd);
  lineChartTitles.push(perWeekLabels.bidsSinceAd);
  lineChartColors.push('yellow');
  lineChartColors.push('emerald');

  const requestsCardTitle = t('requests.title', 'Förfrågningar totalt', { context: 'ad' });
  const bidsCardTitle = t('bids.title', 'Anbud totalt', { context: 'ad' });
  const averageWonProcurementsCardTitle = t('averageWonProcurements.title', 'Vunna upphandlingar totalt', { context: 'ad' });

  const chartDataAfterAd = chartData.filter(x => x.isAfterAd);
  const chartDataBeforeAd = chartData.filter(x => !x.isAfterAd);

  const averageRequestsPerWeekAfterAd = +((chartDataAfterAd.reduce((result, current) => result + (current?.[perWeekLabels.requestsSinceAd] as number || 0), 0) / (chartDataAfterAd.length || 1))).toFixed(2);
  const averageRequestsPerWeekBeforeAd = +((chartDataBeforeAd.reduce((result, current) => result + (current?.[perWeekLabels.requests] as number || 0), 0) / (chartDataBeforeAd.length || 1))).toFixed(2);
  const averageTendersPerWeekAfterAd = +((chartDataAfterAd.reduce((result, current) => result + (current?.[perWeekLabels.bidsSinceAd] as number || 0), 0) / (chartDataAfterAd.length || 1))).toFixed(2);
  const averageTendersPerWeekBeforeAd = +((chartDataBeforeAd.reduce((result, current) => result + (current?.[perWeekLabels.bids] as number || 0), 0) / (chartDataBeforeAd.length || 1))).toFixed(2);

  const adWeekString = moment(adStartDate).format('W GGGG');
  const [adWeek, adYear] = adWeekString.split(' ').map(x => parseInt(x));
  const adDateString = adStartDate ? parseInt(`${adYear}${adWeek < 10 ? `0${adWeek}` : adWeek}`) : 0;
  const isAfterAd = (d: string | Date) => {
    const [, week, year] = moment(d).format(weekFormat).split(' ').map(x => parseInt(x));
    return !!adDateString && adDateString <= parseInt(`${year}${week < 10 ? `0${week}` : week}`);
  };

  // Count the weeks spanning requests + won purchases without a request, split before/after ad.
  const wonRelevantTimes = [
    ...requests.map((r) => new Date(r.date).getTime()),
    ...wonPurchaseDates.map((d) => new Date(d).getTime()),
  ];
  let wonWeeksAfterAd = 0;
  let wonWeeksBeforeAd = 0;
  if (wonRelevantTimes.length) {
    let date = moment(Math.min(...wonRelevantTimes));
    const lastWeek = moment(Math.max(...wonRelevantTimes)).format(weekFormat);
    let week = "";
    let i = 0;
    while (week != lastWeek && i < 10000) {
      week = date.format(weekFormat);
      if (isAfterAd(date.toDate())) wonWeeksAfterAd++;
      else wonWeeksBeforeAd++;
      date = date.add(7, "days");
      i++;
    }
  }

  const wonRequests = requests.filter(r => r.won);
  const wonAfterAd = wonRequests.filter(r => isAfterAd(r.date)).length + wonPurchaseDates.filter(isAfterAd).length;
  const wonBeforeAd = wonRequests.filter(r => !isAfterAd(r.date)).length + wonPurchaseDates.filter(d => !isAfterAd(d)).length;
  const averageWonCategoriesPerWeekAfterAd = +((wonAfterAd / (wonWeeksAfterAd || 1)).toFixed(2));
  const averageWonCategoriesPerWeekBeforeAd = +((wonBeforeAd / (wonWeeksBeforeAd || 1)).toFixed(2));

  const percentageIncreasedRequests = ((averageRequestsPerWeekAfterAd - averageRequestsPerWeekBeforeAd) / (averageRequestsPerWeekBeforeAd || 1)) * 100;
  const percentageIncreasedTenders = ((averageTendersPerWeekAfterAd - averageTendersPerWeekBeforeAd) / (averageTendersPerWeekBeforeAd || 1)) * 100;
  const percentageIncreasedWonCategories = ((averageWonCategoriesPerWeekAfterAd - averageWonCategoriesPerWeekBeforeAd) / (averageWonCategoriesPerWeekBeforeAd || 1)) * 100;

  return (
    <div style={{
      marginBottom: "16px",
      width: "100%",
      display: "grid",
      gridTemplateColumns: '',
      gridTemplateRows: `128px 1fr 1fr`,
      gap: "16px",
    }} className={styles2.pageDashboardPreflightTailwindStyles}>
      <SummaryCard
        title={requestsCardTitle}
        value={averageRequestsPerWeekBeforeAd.toFixed(2).replace('.', ',')}
        value2={averageRequestsPerWeekAfterAd.toFixed(2).replace('.', ',')}
        badgePercentage={percentageIncreasedRequests > 0 ? percentageIncreasedRequests : undefined}
      />
      <SummaryCard
        title={bidsCardTitle}
        value={averageTendersPerWeekBeforeAd.toFixed(2).replace('.', ',')}
        value2={averageTendersPerWeekAfterAd.toFixed(2).replace('.', ',')}
        badgePercentage={percentageIncreasedTenders > 0 ? percentageIncreasedTenders : undefined}
      />
      <SummaryCard
        title={averageWonProcurementsCardTitle}
        value={averageWonCategoriesPerWeekBeforeAd.toFixed(2).replace('.', ',')}
        value2={averageWonCategoriesPerWeekAfterAd.toFixed(2).replace('.', ',')}
        badgePercentage={percentageIncreasedWonCategories > 0 ? percentageIncreasedWonCategories : undefined}
      />

      <LineChart
        title={t('perWeek.title', 'Förfrågningar och anbud per vecka')}
        categories={lineChartTitles}
        index={perWeekLabels.week}
        data={chartData}
        colors={lineChartColors}
        style={{ gridColumnStart: 1, gridColumnEnd: 4 }}
      />
    </div>
  );
}

export default ReceivedBidRequestStats;
